/**
 * FinanceAddRecordPage — full-page create form for a company Invoice or Contract.
 * Route: /dashboard/finance/records/new?type=invoice|contract
 * Replaces the legacy modal that used to live inside Finance.js.
 * Iter-211.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Receipt, FileSignature, Upload, Paperclip, Loader2, CheckCircle,
  AlertCircle, Megaphone, Bot, Server, Share2, MessageSquare, Mail,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_CATEGORIES = [
  { id: 'marketing',    name_ar: 'مدفوعات التسويق',            name_en: 'Marketing',       icon: Megaphone },
  { id: 'ai',           name_ar: 'مدفوعات الذكاء الاصطناعي',    name_en: 'AI Services',     icon: Bot },
  { id: 'servers',      name_ar: 'مدفوعات الخوادم',            name_en: 'Servers',         icon: Server },
  { id: 'social-media', name_ar: 'مدفوعات برامج التواصل',       name_en: 'Social Media',    icon: Share2 },
  { id: 'sms',          name_ar: 'مدفوعات الرسائل النصية',      name_en: 'SMS',             icon: MessageSquare },
  { id: 'email',        name_ar: 'مدفوعات البريد الإلكتروني',   name_en: 'Email',           icon: Mail },
];

const EMPTY_INSTALLMENT = { amount: '', date: '', is_paid: false, attachment: null, attachmentName: '' };
const EMPTY_FORM = {
  title: '', category: '', date: '', amount: '', description: '',
  attachment: null, attachmentName: '',
  isFullyPaid: true, installmentsCount: 1, installments: [],
};

export default function FinanceAddRecordPage() {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') === 'contract' ? 'contract' : 'invoice';
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [invoiceTitles, setInvoiceTitles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingInstallmentFile, setUploadingInstallmentFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Fetch invoice titles (dropdown source)
  useEffect(() => {
    if (!token) return;
    axios.get(`${BACKEND_URL}/api/finance/invoice-titles`, { headers })
      .then((res) => setInvoiceTitles(res.data.data || []))
      .catch(() => {});
  }, [token]);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/upload/file`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setForm((p) => ({ ...p, attachment: res.data.file_id, attachmentName: file.name }));
      toast.success(t('تم رفع الملف بنجاح', 'File uploaded'));
    } catch {
      toast.error(t('فشل رفع الملف', 'Upload failed'));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleInstallmentFileUpload = async (file, index) => {
    if (!file) return;
    setUploadingInstallmentFile(index);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/upload/file`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const next = [...form.installments];
      next[index] = { ...next[index], attachment: res.data.file_id, attachmentName: file.name };
      setForm((p) => ({ ...p, installments: next }));
      toast.success(t('تم رفع مرفق الدفعة', 'Installment file uploaded'));
    } catch {
      toast.error(t('فشل رفع الملف', 'Upload failed'));
    } finally {
      setUploadingInstallmentFile(null);
    }
  };

  const handleInstallmentsCountChange = (count) => {
    const next = Array.from({ length: count }, (_, i) => (form.installments[i] || { ...EMPTY_INSTALLMENT }));
    setForm((p) => ({ ...p, installmentsCount: count, installments: next }));
  };

  const handleInstallmentChange = (index, field, value) => {
    const next = [...form.installments];
    next[index] = { ...next[index], [field]: value };
    setForm((p) => ({ ...p, installments: next }));
  };

  const handleSubmit = async () => {
    if (!form.title)   return toast.error(t('يرجى اختيار عنوان', 'Please pick a title'));
    if (!form.category)return toast.error(t('يرجى اختيار الفئة', 'Please pick a category'));
    if (!form.date)    return toast.error(t('يرجى إدخال التاريخ', 'Please enter the date'));
    if (!form.amount || parseFloat(form.amount) <= 0) {
      return toast.error(t('يرجى إدخال قيمة صحيحة', 'Please enter a valid amount'));
    }

    setIsSubmitting(true);
    try {
      const endpoint = type === 'invoice' ? 'invoices' : 'contracts';
      const payload = {
        title: form.title,
        category: form.category,
        date: form.date,
        amount: parseFloat(form.amount),
        description: form.description,
        attachment: form.attachment,
        is_fully_paid: form.isFullyPaid,
        installments: form.isFullyPaid ? [] : form.installments.map((inst) => ({
          amount: parseFloat(inst.amount) || 0,
          date: inst.date,
          is_paid: false,
          attachment: inst.attachment || null,
        })),
      };
      await axios.post(`${BACKEND_URL}/api/finance/${endpoint}`, payload, { headers });
      toast.success(type === 'invoice'
        ? t('تم إدراج الفاتورة بنجاح', 'Invoice saved')
        : t('تم إدراج العقد بنجاح', 'Contract saved'));
      navigate('/dashboard/finance');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('حدث خطأ أثناء الحفظ', 'Failed to save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const Icon = type === 'invoice' ? Receipt : FileSignature;
  const iconColor = type === 'invoice' ? 'text-emerald-500' : 'text-blue-500';
  const submitBg = type === 'invoice' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700';
  const titleAr = type === 'invoice' ? 'إدراج فاتورة جديدة' : 'إدراج عقد جديد';
  const titleEn = type === 'invoice' ? 'Add New Invoice' : 'Add New Contract';

  return (
    <div className="min-h-screen bg-[#030303] relative pb-20" data-testid="finance-add-record-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance" />

      <div className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: titleAr, label_en: titleEn, path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 mb-8"
        >
          <div className={`w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center ${iconColor}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">{isAr ? titleAr : titleEn}</h1>
            <p className="text-white/50 text-sm mt-1">
              {t('املأ الحقول التالية لتسجيل السجل في القسم المالي', 'Fill in the fields below to record this in Finance')}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6 md:p-8 space-y-6"
        >
          {/* Title Selection */}
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
              {t(`عنوان ${type === 'invoice' ? 'الفاتورة' : 'العقد'} *`, 'Title *')}
            </label>
            <select
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              className="w-full bg-[#0a0a0a] border border-white/10 text-white px-4 py-3 rounded-xl focus:border-emerald-500 focus:outline-none"
              style={{ colorScheme: 'dark' }}
              data-testid="record-title"
            >
              <option value="">{t('-- اختر العنوان --', '-- Select title --')}</option>
              {invoiceTitles.map((tt) => (
                <option key={tt.id} value={tt.title}>{tt.title}</option>
              ))}
            </select>
            {invoiceTitles.length === 0 && (
              <p className="text-xs text-amber-400 mt-2">
                {t('لا توجد عناوين. يرجى إضافتها من لوحة التحكم.', 'No titles found. Please add them in Control Panel.')}
              </p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">{t('الفئة *', 'Category *')}</label>
            <select
              value={form.category}
              onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
              className="w-full bg-[#0a0a0a] border border-white/10 text-white px-4 py-3 rounded-xl focus:border-emerald-500 focus:outline-none"
              style={{ colorScheme: 'dark' }}
              data-testid="record-category"
            >
              <option value="">{t('-- اختر الفئة --', '-- Select category --')}</option>
              {PAYMENT_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>{isAr ? cat.name_ar : cat.name_en}</option>
              ))}
            </select>
          </div>

          {/* Date + Amount */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">{t('التاريخ *', 'Date *')}</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="bg-[#0a0a0a] border-white/10 text-white"
                data-testid="record-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">{t('القيمة *', 'Amount *')}</label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="0.00"
                className="bg-[#0a0a0a] border-white/10 text-white"
                data-testid="record-amount"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
              {t('الوصف (اختياري)', 'Description (optional)')}
            </label>
            <Input
              type="text"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder={t('وصف مختصر...', 'Short description...')}
              className="bg-[#0a0a0a] border-white/10 text-white"
              data-testid="record-description"
            />
          </div>

          {/* Attachment */}
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
              {t('إدراج مرفق (PDF أو صورة)', 'Attachment (PDF or image)')}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="bg-[#0a0a0a] border border-white/10 text-white hover:bg-white/5"
                data-testid="record-attach-btn"
              >
                {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Upload className="h-4 w-4 me-2" />}
                {form.attachment ? t('تغيير الملف', 'Change file') : t('رفع ملف', 'Upload file')}
              </Button>
              {form.attachmentName && (
                <span className="text-sm text-emerald-400 flex items-center gap-1">
                  <Paperclip className="h-4 w-4" />
                  {form.attachmentName}
                </span>
              )}
            </div>
          </div>

          {/* Is Fully Paid toggle */}
          <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
            <p className="text-white font-medium mb-3">{t('هل تم دفع المبلغ بالكامل؟', 'Is the amount fully paid?')}</p>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setForm((p) => ({ ...p, isFullyPaid: true, installments: [] }))}
                className={`flex-1 ${form.isFullyPaid ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-white/5 hover:bg-white/10 text-white/70'}`}
                data-testid="record-paid-yes"
              >
                <CheckCircle className="h-4 w-4 me-2" />
                {t('نعم', 'Yes')}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setForm((p) => ({ ...p, isFullyPaid: false }));
                  handleInstallmentsCountChange(1);
                }}
                className={`flex-1 ${!form.isFullyPaid ? 'bg-amber-600 hover:bg-amber-700' : 'bg-white/5 hover:bg-white/10 text-white/70'}`}
                data-testid="record-paid-no"
              >
                <AlertCircle className="h-4 w-4 me-2" />
                {t('لا', 'No')}
              </Button>
            </div>
          </div>

          {/* Installments (only when not fully paid) */}
          {!form.isFullyPaid && (
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-amber-500/30 space-y-4">
              <div>
                <label className="block text-sm font-medium text-amber-400 mb-2">
                  {t('كم دفعة باقية؟ (حد أقصى 6)', 'How many remaining installments? (max 6)')}
                </label>
                <select
                  value={form.installmentsCount}
                  onChange={(e) => handleInstallmentsCountChange(parseInt(e.target.value, 10))}
                  className="w-full bg-[#1a1a2e] border border-white/10 text-white px-4 py-3 rounded-xl"
                  style={{ colorScheme: 'dark' }}
                  data-testid="record-installments-count"
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{t(`${n} دفعة`, `${n} installments`)}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                {form.installments.map((inst, idx) => (
                  <div key={idx} className="bg-[#1a1a2e] rounded-xl p-3 border border-white/5">
                    <p className="text-sm text-amber-400 mb-2">{t(`الدفعة ${idx + 1}`, `Installment ${idx + 1}`)}</p>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-[#606060] mb-1">{t('المبلغ', 'Amount')}</label>
                        <Input
                          type="number"
                          value={inst.amount}
                          onChange={(e) => handleInstallmentChange(idx, 'amount', e.target.value)}
                          placeholder="0.00"
                          className="bg-[#0a0a0a] border-white/10 text-white text-sm"
                          data-testid={`installment-amount-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#606060] mb-1">{t('تاريخ الاستحقاق', 'Due date')}</label>
                        <Input
                          type="date"
                          value={inst.date}
                          onChange={(e) => handleInstallmentChange(idx, 'date', e.target.value)}
                          className="bg-[#0a0a0a] border-white/10 text-white text-sm"
                          data-testid={`installment-date-${idx}`}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        id={`inst-file-${idx}`}
                        onChange={(e) => e.target.files[0] && handleInstallmentFileUpload(e.target.files[0], idx)}
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => document.getElementById(`inst-file-${idx}`).click()}
                        disabled={uploadingInstallmentFile === idx}
                        className="bg-[#0a0a0a] border border-white/10 text-white hover:bg-white/5"
                      >
                        {uploadingInstallmentFile === idx
                          ? <Loader2 className="h-3 w-3 animate-spin me-1" />
                          : <Paperclip className="h-3 w-3 me-1" />}
                        {t('مرفق', 'Attach')}
                      </Button>
                      {inst.attachmentName && (
                        <span className="text-xs text-emerald-400">{inst.attachmentName}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-white/5">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/finance')}
              className="flex-1 sm:flex-none border-white/10 text-white hover:bg-white/5 rounded-xl"
              data-testid="record-cancel"
            >
              {t('إلغاء', 'Cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`flex-1 rounded-xl ${submitBg}`}
              data-testid="record-submit"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <CheckCircle className="h-4 w-4 me-2" />}
              {t('حفظ', 'Save')}
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
